import {
  Alert,
  Autocomplete,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { TYPE_NOTIFICATION } from "../../../components/reusable/NotificationBull";
import useToast from "../../../hooks/useToast";
import CandidatureService from "../../../services/CandidatureService";

const INIT_FORM_ERROR = {
  id_min_diplome_error: null,
};

export const DIPLOMES = [
  { label: "CEPE", id_diplome: "1" },
  { label: "BEPC", id_diplome: "2" },
  { label: "Bac", id_diplome: "3" },
  { label: "Bac +2 / DTS", id_diplome: "7" },
  { label: "Bac +3 / Licence", id_diplome: "8" },
  { label: "Bac +4 / Master 1", id_diplome: "9" },
  { label: "Bac +5 / Master 2 / Ingénieur", id_diplome: "10" },
  { label: "DEA", id_diplome: "11" },
  { label: "AEA", id_diplome: "12" },
  { label: "Doctorat", id_diplome: "13" },
];

const DiplomeRequisPage = forwardRef(({ titre }, ref) => {
  const [formLoading, setFormLoading] = useState(false);
  const navigate = useNavigate();
  const [amiInfo, setAmiInfo] = useState(null);
  const [formError, setFormError] = useState(INIT_FORM_ERROR);
  const [searchParams] = useSearchParams();
  const id_ami = searchParams.get("id_type");
  const { Toast, showToast } = useToast();
  const showToastRef = useRef(showToast);

  useEffect(() => {
    showToastRef.current = showToast;
  }, [showToast]);

  const selectedDiplome =
    DIPLOMES.find((d) => d.id_diplome === amiInfo?.id_min_diplom) || null;

  const validate = () => {
    const errors = {
      id_min_diplome_error: null,
    };
    setFormError(errors);
    return Object.values(errors).every((error) => error === null);
  };

  const save = async () => {
    if (!validate()) {
      showToast(
        TYPE_NOTIFICATION.ERROR,
        "Veuillez corriger les erreurs dans le formulaire avant de continuer."
      );
      return false;
    }

    const payload = {
      ...amiInfo,
      id_type: id_ami || undefined,
    };

    const result = await CandidatureService.insert(payload);

    if (result && result.success) {
      if (result.data) {
        setAmiInfo(result.data);
        searchParams.set("id_type", result.data.id_type);
        window.history.replaceState(null, "", "?" + searchParams.toString());
        navigate(`?id_type=${result.data.id_type}`, { replace: true });
      }
      showToast(
        TYPE_NOTIFICATION.SUCCESS,
        "Les informations de l'AMI ont été enregistrées avec succès."
      );
      return true;
    }

    showToast(
      TYPE_NOTIFICATION.ERROR,
      "Une erreur est survenue lors de l'enregistrement des informations de l'AMI."
    );
    return false;
  };

  useImperativeHandle(ref, () => ({
    save,
  }));

  const handleFetchAMIInfo = useCallback((idAmi) => {
    if (!idAmi) return;
    setFormLoading(true);
    CandidatureService.find_one(idAmi)
      .then((res) => {
        if (res && res.data) {
          setAmiInfo(res.data);
        } else {
          showToastRef.current(
            TYPE_NOTIFICATION.ERROR,
            "Impossible de récupérer les informations de l'AMI."
          );
        }
      })
      .finally(() => {
        setFormLoading(false);
      });
  }, []);

  useEffect(() => {
    handleFetchAMIInfo(id_ami);
  }, [handleFetchAMIInfo, id_ami]);

  return (
    <>
      <Toast />
      <Box sx={{ width: "100%", p: 2 }}>
        <Card
          elevation={0}
          sx={{
            borderRadius: 2,
            border: 1,
            borderColor: "divider",
            background: "linear-gradient(180deg, #F8FAFC 0%, #FFFFFF 100%)",
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              spacing={2}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "primary.main",
                    backgroundColor: "#EAF2FF",
                    flexShrink: 0,
                  }}
                >
                  <SchoolOutlinedIcon />
                </Box>
                <Box>
                  <Typography variant="overline" color="primary" fontWeight={700}>
                    Préqualification
                  </Typography>
                  <Typography variant="h5" fontWeight={800}>
                    {titre || "Diplôme minimum requis"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Définissez le niveau minimum attendu pour cet AMI.
                  </Typography>
                </Box>
              </Stack>
              <Chip
                label={selectedDiplome?.label || "Facultatif"}
                color={selectedDiplome ? "primary" : "default"}
                variant="outlined"
                sx={{ fontWeight: 700 }}
              />
            </Stack>

            <Divider sx={{ my: 2.5 }} />

            <Box
              sx={{
                display: formLoading ? "flex" : "none",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "30vh",
              }}
            >
              {formLoading && (
                <CircularProgress size="3rem" aria-label="Chargement" />
              )}
            </Box>

            {!formLoading && (
              <Grid container spacing={2}>
                {!amiInfo?.id_min_diplom && (
                  <Grid item xs={12}>
                    <Alert
                      severity="info"
                      variant="outlined"
                      sx={{ borderRadius: 2, backgroundColor: "#FFFFFF" }}
                    >
                      Le diplôme minimum requis est facultatif. Laissez vide si
                      aucun niveau minimum n'est nécessaire.
                    </Alert>
                  </Grid>
                )}
                <Grid item xs={12} md={8}>
                  <Autocomplete
                    options={DIPLOMES}
                    size="small"
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) =>
                      option.id_diplome === value.id_diplome
                    }
                    onChange={(_, value) => {
                      setAmiInfo({
                        ...amiInfo,
                        id_min_diplom: value?.id_diplome || null,
                      });
                    }}
                    value={selectedDiplome}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Diplôme minimum requis"
                        error={formError.id_min_diplome_error !== null}
                        helperText={formError.id_min_diplome_error}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            )}
          </CardContent>
        </Card>
      </Box>
    </>
  );
});

export default DiplomeRequisPage;
